import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { getPartnerAuthHeaders } from "@/lib/apiAuth";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import PartnerPageHeader from "../components/PartnerPageHeader";
import PartnerBillingSummary from "../components/PartnerBillingSummary";
import { WHOLESALE_PRICE_SUMMARY } from "@/constants/aiTiersCatalog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const extractErrorMessage = (err, fallback) => {
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  }
  if (err?.response?.data?.message) return err.response.data.message;
  if (err?.request && !err?.response) {
    return "Network error. Please check your connection and try again.";
  }
  return fallback;
};

const BillingSkeleton = () => (
  <div
    className="rounded-2xl border border-partner bg-[#11151F] p-8 animate-pulse"
    data-testid="partner-billing-skeleton"
  >
    <div className="h-6 w-48 bg-white/10 rounded mb-6" />
    <div className="space-y-3">
      <div className="h-4 w-2/3 bg-white/10 rounded" />
      <div className="h-4 w-1/2 bg-white/10 rounded" />
    </div>
  </div>
);

const LoadError = ({ message, onRetry, isRetrying }) => (
  <div
    className="rounded-2xl border border-red-500/30 bg-red-500/5 p-8 text-center"
    data-testid="partner-billing-load-error"
  >
    <AlertTriangle className="mx-auto h-10 w-10 text-red-400 mb-4" />
    <h2 className="text-xl font-bold text-partner mb-2">Couldn&apos;t load billing</h2>
    <p className="text-sm text-partner-muted font-body max-w-md mx-auto">{message}</p>
    <button
      type="button"
      onClick={onRetry}
      disabled={isRetrying}
      className="mt-6 inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold bg-white/10 text-partner hover:bg-white/20 disabled:opacity-50"
      data-testid="partner-billing-retry-button"
    >
      {isRetrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
      Retry
    </button>
  </div>
);

const PartnerBilling = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [billing, setBilling] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState("");
  const [returnNotice, setReturnNotice] = useState("");

  const getAuthHeader = useCallback(() => getPartnerAuthHeaders(), []);

  const fetchSummary = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const res = await axios.get(`${BACKEND_URL}/api/partner/billing/summary`, {
        headers: getAuthHeader(),
      });
      setBilling(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        Cookies.remove("token");
        Cookies.remove("email");
        navigate("/login");
        return;
      }
      setLoadError(
        extractErrorMessage(err, "Something went wrong while loading billing. Please try again.")
      );
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeader, navigate]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    const status = searchParams.get("status");
    if (status === "success") {
      setReturnNotice("Payment method saved. Wholesale billing is ready.");
      fetchSummary();
    } else if (status === "canceled") {
      setReturnNotice("Payment setup was canceled. You can try again when ready.");
    }
    if (status) {
      searchParams.delete("status");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, fetchSummary]);

  const redirectToStripe = async (endpoint) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setActionError("");
    try {
      const res = await axios.post(`${BACKEND_URL}${endpoint}`, {}, { headers: getAuthHeader() });
      const url = res?.data?.url;
      if (!url) throw new Error("Invalid Stripe response");
      window.location.href = url;
    } catch (err) {
      if (err?.response?.status === 401) {
        Cookies.remove("token");
        Cookies.remove("email");
        navigate("/login");
        return;
      }
      setActionError(
        extractErrorMessage(err, "Could not open Stripe. Please try again.")
      );
      setIsSubmitting(false);
    }
  };

  const handleSetup = () => redirectToStripe("/api/partner/billing/setup");
  const handlePortal = () => redirectToStripe("/api/partner/billing/portal");

  return (
    <div data-testid="partner-billing">
      <PartnerPageHeader
        tag="Wholesale billing"
        title="Partner"
        titleAccent="billing"
        subtitle={`Track wholesale infrastructure costs (${WHOLESALE_PRICE_SUMMARY} per active client tier). Retail billing to your end clients stays outside this platform.`}
      />

      {returnNotice && (
        <div
          className="mb-6 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300 font-body"
          data-testid="partner-billing-return-notice"
        >
          {returnNotice}
        </div>
      )}

      {isLoading && <BillingSkeleton />}

      {!isLoading && loadError && (
        <LoadError message={loadError} onRetry={fetchSummary} isRetrying={isLoading} />
      )}

      {!isLoading && !loadError && billing && (
        <PartnerBillingSummary
          billing={billing}
          isSubmitting={isSubmitting}
          onSetup={handleSetup}
          onPortal={handlePortal}
          actionError={actionError}
        />
      )}
    </div>
  );
};

export default PartnerBilling;
